import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography, Paper, IconButton } from "@mui/material";
import { useAppStateContext } from "../../state/AppContextProvider";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

export interface FAQItem {
    id?: string;
    question: string;
    answer: string;
}

const FAQManager: React.FC = () => {
    const { faqs, addFAQ, updateFAQ, deleteFAQ } = useAppStateContext() as any;
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editQuestion, setEditQuestion] = useState("");
    const [editAnswer, setEditAnswer] = useState("");
    const [newQuestion, setNewQuestion] = useState("");
    const [newAnswer, setNewAnswer] = useState("");

    const isEditing = editingId !== null;

    const handleFormSubmit = async () => {
        if (!newQuestion.trim() || !newAnswer.trim()) return;
        if (isEditing) {
            await updateFAQ({ id: editingId, question: newQuestion, answer: newAnswer }, new AbortController().signal);
        } else {
            await addFAQ({ question: newQuestion, answer: newAnswer }, new AbortController().signal);
        }
        setEditingId(null);
        setNewQuestion("");
        setNewAnswer("");
    };

    const handleEditFAQ = (faq: FAQItem) => {
        setEditingId(faq.id || "");
        setNewQuestion(faq.question);
        setNewAnswer(faq.answer);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setNewQuestion("");
        setNewAnswer("");
    };

    const handleDeleteFAQ = async (faq: FAQItem) => {
        if (!faq.id) return;
        await deleteFAQ(faq, new AbortController().signal);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Manage FAQs</Typography>
            <Stack spacing={2}>
                {/* Add New FAQ */}
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">{isEditing ? "Edit FAQ" : "Add New FAQ"}</Typography>
                    <TextField
                        label="Question"
                        value={newQuestion}
                        onChange={e => setNewQuestion(e.target.value)}
                        fullWidth
                        sx={{ mb: 1 }}
                    />
                    <TextField
                        label="Answer"
                        value={newAnswer}
                        onChange={e => setNewAnswer(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mb: 1 }}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={handleFormSubmit}>
                            {isEditing ? "Save" : "Add FAQ"}
                        </Button>
                        {isEditing && (
                            <Button variant="outlined" color="secondary" onClick={handleCancelEdit}>
                                Cancel
                            </Button>
                        )}
                    </Stack>
                </Paper>
                {/* FAQ List */}
                <Box sx={{ maxHeight: "22vh", overflowY: "auto" }}>
                    {faqs?.map((faq: FAQItem) => (
                        <Paper key={faq.id || faq.question} sx={{ p: 2, display: "flex", alignItems: "center", mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1">{faq.question}</Typography>
                                <Typography variant="body2">{faq.answer}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <IconButton
                                    onClick={() => handleEditFAQ(faq)}
                                    sx={{ color: "#1976d2" }} // Blue pencil
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => handleDeleteFAQ(faq)} color="error"><DeleteIcon /></IconButton>
                            </Stack>
                        </Paper>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
};

export default FAQManager;